import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { CategoryNode, CompanyDashboard, Impact, SlaPreview, Urgency, WorkItemCard } from '../lib/types';
import { IMPACT_LABELS, URGENCY_LABELS } from '../lib/types';
import { formatWhen } from '../lib/dates';

const IMPACTS: Impact[] = ['organisation', 'department', 'group', 'individual'];
const URGENCIES: Urgency[] = ['critical', 'high', 'medium', 'low'];

/** What the customer is telling us, in their words rather than ours. */
const KINDS = [
  { type: 'bug' as const, label: 'Something is broken', hint: 'It used to work, or it is not doing what it should.' },
  { type: 'task' as const, label: 'I need something', hint: 'A change, a new thing, or a piece of work from us.' },
];

/**
 * Raising a request, as a customer.
 *
 * The last piece of the Halo loop. The clocks, the waiting state, resolve-then-close and the
 * service categories were all built already — but they only ever started on work we raised on
 * a customer's behalf, because nothing asked the customer the two questions the whole
 * mechanism turns on.
 *
 * Impact and urgency are asked separately and the matrix decides the priority, because asking
 * somebody to rate their own request's "priority" is how every request becomes critical. And
 * the promise those answers create is shown while they are choosing, not after: seeing that
 * "the whole company, work has stopped" commits us to a reply in thirty minutes is what makes
 * the question worth asking honestly.
 */
export function RaiseRequestPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();

  const [company, setCompany] = useState<CompanyDashboard | null>(null);
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [raised, setRaised] = useState<WorkItemCard | null>(null);

  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<'bug' | 'task'>('bug');
  const [impact, setImpact] = useState<Impact | ''>('');
  const [urgency, setUrgency] = useState<Urgency | ''>('');
  const [categoryId, setCategoryId] = useState('');
  const [promise, setPromise] = useState<SlaPreview | null>(null);

  useEffect(() => {
    if (!companyId) return;
    api
      .get<CompanyDashboard>(`/companies/${companyId}/dashboard`)
      .then((result) => {
        setCompany(result);
        // One system to choose from is not a choice; pick it and say so instead of asking.
        const active = result.projects.filter((project) => project.status === 'active');
        if (active.length === 1) setProjectId(active[0].id);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load this company'));

    api
      .get<CategoryNode[]>(`/companies/${companyId}/categories`)
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [companyId]);

  /** The promise these two answers create, from the same policy a real clock is built on. */
  useEffect(() => {
    if (!companyId || !impact || !urgency) {
      setPromise(null);
      return;
    }
    let current = true;
    api
      .get<SlaPreview>(`/companies/${companyId}/sla-preview?impact=${impact}&urgency=${urgency}`)
      .then((result) => {
        if (current) setPromise(result);
      })
      .catch(() => {
        if (current) setPromise(null);
      });
    return () => {
      current = false;
    };
  }, [companyId, impact, urgency]);

  const projects = useMemo(
    () => (company?.projects ?? []).filter((project) => project.status === 'active'),
    [company],
  );

  const ready = projectId && title.trim() && impact && urgency;

  async function send() {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<{ id: string }>(`/projects/${projectId}/work-items`, {
        title: title.trim(),
        description: description.trim() || undefined,
        type: kind,
        impact,
        urgency,
        categoryId: categoryId || undefined,
      });

      // Read it back rather than trusting the form: the key and the clock are the server's to
      // decide, and they are the two things worth showing.
      const detail = await api.get<{ item: WorkItemCard }>(`/work-items/${created.id}`);
      setRaised(detail.item);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that request');
    } finally {
      setBusy(false);
    }
  }

  if (error && !company) return <div className="callout">{error}</div>;
  if (!company) return <div className="empty">Loading…</div>;

  if (company.capabilities['work.write'] === 'none') {
    return (
      <div className="card">
        <div className="empty">
          <h3>Your role cannot raise requests</h3>
          <p>Ask an administrator to raise this for you, or to change your access.</p>
        </div>
      </div>
    );
  }

  if (raised) {
    return (
      <>
        <div className="page-head">
          <div>
            <div className="eyebrow">{company.company.name}</div>
            <h1>Request sent</h1>
          </div>
        </div>

        <div className="card">
          <div className="card-pad stack">
            <div className="callout callout-ok" style={{ marginBottom: 0 }}>
              <strong>
                {raised.key} — {raised.title}
              </strong>
              <div style={{ marginTop: 6 }}>
                Logged as <strong>{raised.priority}</strong> priority.
                {raised.sla?.response?.due && (
                  <>
                    {' '}
                    We will reply by <strong>{formatWhen(raised.sla.response.due)}</strong>.
                  </>
                )}
                {raised.sla?.resolution?.due && (
                  <> Aiming to have it sorted by <strong>{formatWhen(raised.sla.resolution.due)}</strong>.</>
                )}
              </div>
            </div>

            <div className="row" style={{ gap: 8 }}>
              <Link className="btn btn-primary" to={`/projects/${projectId}/board?task=${raised.id}`}>
                Follow this request
              </Link>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  // Another one, without carrying the last answers over.
                  setRaised(null);
                  setTitle('');
                  setDescription('');
                  setImpact('');
                  setUrgency('');
                  setCategoryId('');
                }}
              >
                Raise another
              </button>
              <Link className="btn btn-quiet" to={`/companies/${company.company.id}/issues`}>
                Everything we are working on
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">
            <Link to={`/companies/${company.company.id}`} className="muted">
              {company.company.name}
            </Link>
          </div>
          <h1>Raise a request</h1>
          <p className="subtitle">
            Tell us what you need and how much it is holding you up. What you choose sets when we
            have promised to reply and to have it sorted.
          </p>
        </div>
        <button type="button" className="btn" onClick={() => navigate(-1)}>
          Cancel
        </button>
      </div>

      {error && (
        <div className="callout mb-2" role="alert">
          {error}
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', alignItems: 'start' }}>
        <div className="card">
          <div className="card-pad stack">
            {projects.length > 1 ? (
              <label className="field">
                <span className="field-label">Which system is this about?</span>
                <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
                  <option value="">Choose one…</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : projects.length === 1 ? (
              <div className="field">
                <span className="field-label">This is about</span>
                <span>{projects[0].name}</span>
              </div>
            ) : (
              <div className="callout">There is no active project on your account to raise this against yet.</div>
            )}

            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="field-label">Is something broken, or do you need something?</legend>
              <div className="row wrap" style={{ gap: 8 }}>
                {KINDS.map((option) => (
                  <label key={option.type} className={kind === option.type ? 'choice is-on' : 'choice'}>
                    <input
                      type="radio"
                      name="kind"
                      value={option.type}
                      checked={kind === option.type}
                      onChange={() => setKind(option.type)}
                    />
                    <span>
                      <strong>{option.label}</strong>
                      <span className="choice-hint">{option.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="field">
              <span className="field-label">What do you need?</span>
              <input
                value={title}
                maxLength={300}
                placeholder="One line, as you would say it to a colleague"
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>

            <label className="field">
              <span className="field-label">Tell us more</span>
              <textarea
                rows={5}
                value={description}
                placeholder="What were you doing, what happened, and anything we would need to see it ourselves."
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>

            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="field-label">Who is affected?</legend>
              <div className="row wrap" style={{ gap: 6 }}>
                {IMPACTS.map((option) => (
                  <label key={option} className={impact === option ? 'choice is-on' : 'choice'}>
                    <input
                      type="radio"
                      name="impact"
                      value={option}
                      checked={impact === option}
                      onChange={() => setImpact(option)}
                    />
                    <span>{IMPACT_LABELS[option]}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="field-label">How much is it holding you up?</legend>
              <div className="row wrap" style={{ gap: 6 }}>
                {URGENCIES.map((option) => (
                  <label key={option} className={urgency === option ? 'choice is-on' : 'choice'}>
                    <input
                      type="radio"
                      name="urgency"
                      value={option}
                      checked={urgency === option}
                      onChange={() => setUrgency(option)}
                    />
                    <span>{URGENCY_LABELS[option]}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {categories.length > 0 && (
              <label className="field">
                <span className="field-label">What is it about? (optional)</span>
                <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
                  <option value="">Not sure</option>
                  {categories.flatMap((parent) => [
                    <option key={parent.id} value={parent.id}>
                      {parent.name}
                    </option>,
                    ...(parent.children ?? []).map((child) => (
                      <option key={child.id} value={child.id}>
                        {parent.name} › {child.name}
                      </option>
                    )),
                  ])}
                </select>
              </label>
            )}

            <div className="row" style={{ gap: 8 }}>
              <button type="button" className="btn btn-primary" disabled={!ready || busy} onClick={send}>
                {busy ? 'Sending…' : 'Send the request'}
              </button>
              {!ready && (
                <span className="dim" style={{ fontSize: 12 }}>
                  A system, a line about what you need, and both answers above.
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>What we are promising</h3>
          </div>
          <div className="card-pad">
            {!impact || !urgency ? (
              <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                Answer who is affected and how much it is holding you up, and this will say when we
                have committed to reply and to have it sorted.
              </p>
            ) : !promise ? (
              <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                Working it out…
              </p>
            ) : (
              <div className="stack-sm" style={{ fontSize: 13 }}>
                <div>
                  Treated as <strong>{promise.priority}</strong> priority.
                </div>
                {promise.agreed ? (
                  <>
                    <div>
                      First reply by <strong>{formatWhen(promise.respondBy!)}</strong>
                    </div>
                    <div>
                      Sorted by <strong>{formatWhen(promise.resolveBy!)}</strong>
                    </div>
                    <p className="dim" style={{ fontSize: 11, margin: '4px 0 0' }}>
                      Counted in working hours only ({promise.hoursInWords ?? 'our working week'}), so a request
                      late in the day carries over.
                    </p>
                  </>
                ) : (
                  <p className="dim" style={{ margin: 0 }}>
                    No response time has been agreed for {promise.priority} work on this account, so
                    this will be worked without a clock.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
